/**
 * CircularGallery - Vanilla JS + OGL Implementation
 * Adapted from React Bits for Shopify / HTML
 * Fixed for CORS, WebGL texture loading, and smooth image rendering.
 */

(function (window) {
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  function lerp(p1, p2, t) {
    return p1 + (p2 - p1) * t;
  }

  function autoBind(instance) {
    const proto = Object.getPrototypeOf(instance);
    Object.getOwnPropertyNames(proto).forEach(key => {
      if (key !== 'constructor' && typeof instance[key] === 'function') {
        instance[key] = instance[key].bind(instance);
      }
    });
  }

  const DEFAULT_FONT = 'bold 24px Outfit, sans-serif';

  // Helper to create a fallback image canvas if external image fails or is loading
  function createPlaceholderCanvas(text, bg1 = '#0f172a', bg2 = '#1e293b') {
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');

    const grad = ctx.createLinearGradient(0, 0, 800, 600);
    grad.addColorStop(0, bg1);
    grad.addColorStop(1, bg2);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 800, 600);

    ctx.strokeStyle = '#00E5FF';
    ctx.lineWidth = 4;
    ctx.strokeRect(20, 20, 760, 560);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px Outfit, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text || 'Skyland Bike', 400, 300);

    return canvas;
  }

  function createTextTexture(gl, text, font = DEFAULT_FONT, color = '#00E5FF') {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    const textWidth = Math.ceil(metrics.width);
    const textHeight = 40;
    canvas.width = textWidth + 40;
    canvas.height = textHeight + 20;

    context.font = font;
    context.fillStyle = color;
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.shadowColor = 'rgba(0, 229, 255, 0.8)';
    context.shadowBlur = 12;
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new window.ogl.Texture(gl, { generateMipmaps: false });
    texture.image = canvas;
    return { texture, width: canvas.width, height: canvas.height };
  }

  class Title {
    constructor({ gl, plane, renderer, text, textColor = '#00E5FF', font = DEFAULT_FONT }) {
      autoBind(this);
      this.gl = gl;
      this.plane = plane;
      this.renderer = renderer;
      this.text = text;
      this.textColor = textColor;
      this.font = font;
      this.createMesh();
    }
    createMesh() {
      const { texture, width, height } = createTextTexture(this.gl, this.text, this.font, this.textColor);
      const geometry = new window.ogl.Plane(this.gl);
      const program = new window.ogl.Program(this.gl, {
        vertex: `
          attribute vec3 position;
          attribute vec2 uv;
          uniform mat4 modelViewMatrix;
          uniform mat4 projectionMatrix;
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragment: `
          precision highp float;
          uniform sampler2D tMap;
          varying vec2 vUv;
          void main() {
            vec4 color = texture2D(tMap, vUv);
            if (color.a < 0.05) discard;
            gl_FragColor = color;
          }
        `,
        uniforms: { tMap: { value: texture } },
        transparent: true
      });
      this.mesh = new window.ogl.Mesh(this.gl, { geometry, program });
      const aspect = width / height;
      const textHeight = this.plane.scale.y * 0.14;
      const textWidth = textHeight * aspect;
      this.mesh.scale.set(textWidth, textHeight, 1);
      this.mesh.position.y = -this.plane.scale.y * 0.5 - textHeight * 0.5 - 0.08;
      this.mesh.setParent(this.plane);
    }
  }

  class Media {
    constructor({
      geometry,
      gl,
      image,
      index,
      length,
      renderer,
      scene,
      screen,
      text,
      url,
      viewport,
      bend,
      textColor,
      borderRadius = 0.05,
      font
    }) {
      this.extra = 0;
      this.geometry = geometry;
      this.gl = gl;
      this.image = image;
      this.index = index;
      this.length = length;
      this.renderer = renderer;
      this.scene = scene;
      this.screen = screen;
      this.text = text;
      this.url = url;
      this.viewport = viewport;
      this.bend = bend;
      this.textColor = textColor;
      this.borderRadius = borderRadius;
      this.font = font;
      this.screenPos = { x: 0, y: 0, w: 0, h: 0 };
      this.createShader();
      this.createMesh();
      this.createTitle();
      this.onResize();
    }
    createShader() {
      const texture = new window.ogl.Texture(this.gl, {
        generateMipmaps: true,
        wrapS: this.gl.CLAMP_TO_EDGE,
        wrapT: this.gl.CLAMP_TO_EDGE
      });

      // Start with placeholder canvas to prevent ANY black box rendering
      texture.image = createPlaceholderCanvas(this.text);

      this.program = new window.ogl.Program(this.gl, {
        depthTest: false,
        depthWrite: false,
        vertex: `
          precision highp float;
          attribute vec3 position;
          attribute vec2 uv;
          uniform mat4 modelViewMatrix;
          uniform mat4 projectionMatrix;
          uniform float uTime;
          uniform float uSpeed;
          varying vec2 vUv;
          void main() {
            vUv = uv;
            vec3 p = position;
            p.z = (sin(p.x * 4.0 + uTime) * 1.2 + cos(p.y * 2.0 + uTime) * 1.2) * (0.08 + uSpeed * 0.4);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
          }
        `,
        fragment: `
          precision highp float;
          uniform vec2 uImageSizes;
          uniform vec2 uPlaneSizes;
          uniform sampler2D tMap;
          uniform float uBorderRadius;
          varying vec2 vUv;
          
          float roundedBoxSDF(vec2 p, vec2 b, float r) {
            vec2 d = abs(p) - b;
            return length(max(d, vec2(0.0))) + min(max(d.x, d.y), 0.0) - r;
          }
          
          void main() {
            vec2 ratio = vec2(
              min((uPlaneSizes.x / uPlaneSizes.y) / (uImageSizes.x / uImageSizes.y), 1.0),
              min((uPlaneSizes.y / uPlaneSizes.x) / (uImageSizes.y / uImageSizes.x), 1.0)
            );
            vec2 uv = vec2(
              vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
              vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
            );
            vec4 color = texture2D(tMap, uv);
            
            float d = roundedBoxSDF(vUv - 0.5, vec2(0.5 - uBorderRadius), uBorderRadius);
            float edgeSmooth = 0.002;
            float alpha = 1.0 - smoothstep(-edgeSmooth, edgeSmooth, d);
            
            gl_FragColor = vec4(color.rgb, alpha);
          }
        `,
        uniforms: {
          tMap: { value: texture },
          uPlaneSizes: { value: [0, 0] },
          uImageSizes: { value: [800, 600] },
          uSpeed: { value: 0 },
          uTime: { value: 100 * Math.random() },
          uBorderRadius: { value: this.borderRadius }
        },
        transparent: true
      });

      // Load actual image via 2D Canvas buffer to guarantee CORS safety, falling back to direct Image element if canvas taints
      if (this.image) {
        const loadDirect = (url) => {
          const directImg = new Image();
          directImg.onload = () => {
            texture.image = directImg;
            this.program.uniforms.uImageSizes.value = [directImg.naturalWidth || 800, directImg.naturalHeight || 600];
          };
          directImg.src = url;
        };

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = 900;
            canvas.height = 675;
            const ctx = canvas.getContext('2d');

            // Studio rounded card background
            const r = 36;
            ctx.fillStyle = '#ffffff';
            if (ctx.roundRect) {
              ctx.beginPath();
              ctx.roundRect(0, 0, 900, 675, r);
              ctx.fill();
            } else {
              ctx.fillRect(0, 0, 900, 675);
            }

            // Draw image centered with padding (contain mode) so wheels and handlebars are never clipped
            const nw = img.naturalWidth || 800;
            const nh = img.naturalHeight || 600;
            const pad = 44;
            const scale = Math.min((900 - pad * 2) / nw, (675 - pad * 2) / nh);
            const dw = nw * scale;
            const dh = nh * scale;
            const dx = (900 - dw) / 2;
            const dy = (675 - dh) / 2;

            ctx.drawImage(img, dx, dy, dw, dh);

            // Add cyan border accent
            ctx.strokeStyle = '#00E5FF';
            ctx.lineWidth = 10;
            if (ctx.roundRect) {
              ctx.beginPath();
              ctx.roundRect(5, 5, 890, 665, r - 5);
              ctx.stroke();
            }

            texture.image = canvas;
            this.program.uniforms.uImageSizes.value = [900, 675];
          } catch (e) {
            console.warn('CORS canvas draw error, falling back to direct img element:', this.image);
            texture.image = img;
            this.program.uniforms.uImageSizes.value = [img.naturalWidth || 800, img.naturalHeight || 600];
          }
        };
        img.onerror = () => {
          console.warn('CORS anonymous load failed, retrying without crossOrigin:', this.image);
          loadDirect(this.image);
        };
        img.src = this.image;
      }
    }
    createMesh() {
      this.plane = new window.ogl.Mesh(this.gl, {
        geometry: this.geometry,
        program: this.program
      });
      this.plane.setParent(this.scene);
    }
    createTitle() {
      this.title = new Title({
        gl: this.gl,
        plane: this.plane,
        renderer: this.renderer,
        text: this.text,
        textColor: this.textColor,
        font: this.font
      });
    }
    update(scroll, direction, camera) {
      this.plane.position.x = this.x - scroll.current - this.extra;

      const x = this.plane.position.x;
      const H = this.viewport.width / 2;

      if (this.bend === 0) {
        this.plane.position.y = 0;
        this.plane.rotation.z = 0;
      } else {
        const B_abs = Math.abs(this.bend);
        const R = (H * H + B_abs * B_abs) / (2 * B_abs);
        const effectiveX = Math.min(Math.abs(x), H);

        const arc = R - Math.sqrt(R * R - effectiveX * effectiveX);
        if (this.bend > 0) {
          this.plane.position.y = -arc;
          this.plane.rotation.z = -Math.sign(x) * Math.asin(effectiveX / R);
        } else {
          this.plane.position.y = arc;
          this.plane.rotation.z = Math.sign(x) * Math.asin(effectiveX / R);
        }
      }

      this.speed = scroll.current - scroll.last;
      this.program.uniforms.uTime.value += 0.04;
      this.program.uniforms.uSpeed.value = this.speed;

      const planeOffset = this.plane.scale.x / 2;
      const viewportOffset = this.viewport.width / 2;
      this.isBefore = this.plane.position.x + planeOffset < -viewportOffset;
      this.isAfter = this.plane.position.x - planeOffset > viewportOffset;
      if (direction === 'right' && this.isBefore) {
        this.extra -= this.widthTotal;
        this.isBefore = this.isAfter = false;
      }
      if (direction === 'left' && this.isAfter) {
        this.extra += this.widthTotal;
        this.isBefore = this.isAfter = false;
      }

      if (camera && this.screen) {
        const projX = (this.plane.position.x / this.viewport.width) * this.screen.width;
        const projY = (-this.plane.position.y / this.viewport.height) * this.screen.height;
        const cardW = (this.plane.scale.x / this.viewport.width) * this.screen.width;
        const cardH = (this.plane.scale.y / this.viewport.height) * this.screen.height;

        this.screenPos = {
          x: this.screen.width / 2 + projX - cardW / 2,
          y: this.screen.height / 2 + projY - cardH / 2,
          w: cardW,
          h: cardH
        };
      }
    }
    onResize({ screen, viewport } = {}) {
      if (screen) this.screen = screen;
      if (viewport) {
        this.viewport = viewport;
      }
      this.scale = this.screen.height / 1400;
      this.plane.scale.y = (this.viewport.height * (560 * this.scale)) / this.screen.height;
      this.plane.scale.x = (this.viewport.width * (680 * this.scale)) / this.screen.width;
      this.plane.program.uniforms.uPlaneSizes.value = [this.plane.scale.x, this.plane.scale.y];
      this.padding = 2.0;
      this.width = this.plane.scale.x + this.padding;
      this.widthTotal = this.width * this.length;
      this.x = this.width * this.index;
    }
  }

  class CircularGalleryApp {
    constructor(container, options = {}) {
      if (!window.ogl) {
        console.error('CircularGallery requires OGL library (window.ogl)');
        return;
      }
      this.container = container;
      this.items = options.items || [];
      this.bend = options.bend !== undefined ? options.bend : 3;
      this.textColor = options.textColor || '#00E5FF';
      this.borderRadius = options.borderRadius !== undefined ? options.borderRadius : 0.05;
      this.font = options.font || DEFAULT_FONT;
      this.scrollSpeed = options.scrollSpeed || 2;
      this.scrollEase = options.scrollEase || 0.05;

      this.scroll = { ease: this.scrollEase, current: 0, target: 0, last: 0 };
      this.onCheckDebounce = debounce(this.onCheck.bind(this), 200);

      this.init();
    }

    init() {
      this.container.style.position = 'relative';
      this.container.style.overflow = 'hidden';

      this.createRenderer();
      this.createCamera();
      this.createScene();
      this.onResize();
      this.createGeometry();
      this.createMedias();
      this.update();
      this.addEventListeners();
    }

    createRenderer() {
      this.renderer = new window.ogl.Renderer({
        alpha: true,
        antialias: true,
        dpr: Math.min(window.devicePixelRatio || 1, 2)
      });
      this.gl = this.renderer.gl;
      this.gl.clearColor(0, 0, 0, 0);
      this.container.appendChild(this.gl.canvas);
      this.gl.canvas.style.display = 'block';
      this.gl.canvas.style.width = '100%';
      this.gl.canvas.style.height = '100%';
    }

    createCamera() {
      this.camera = new window.ogl.Camera(this.gl);
      this.camera.fov = 45;
      this.camera.position.z = 20;
    }

    createScene() {
      this.scene = new window.ogl.Transform();
    }

    createGeometry() {
      this.planeGeometry = new window.ogl.Plane(this.gl, {
        heightSegments: 50,
        widthSegments: 100
      });
    }

    createMedias() {
      const galleryItems = this.items.length ? this.items : [
        { image: 'https://via.placeholder.com/800x600/10B981/ffffff?text=Skyland+Murano', text: 'Skyland Murano 5', url: '#' }
      ];
      this.mediasImages = galleryItems.length < 6 ? galleryItems.concat(galleryItems).concat(galleryItems) : galleryItems.concat(galleryItems);

      this.medias = this.mediasImages.map((data, index) => {
        return new Media({
          geometry: this.planeGeometry,
          gl: this.gl,
          image: data.image,
          index,
          length: this.mediasImages.length,
          renderer: this.renderer,
          scene: this.scene,
          screen: this.screen,
          text: data.text || data.title,
          url: data.url || data.href || '#',
          viewport: this.viewport,
          bend: this.bend,
          textColor: this.textColor,
          borderRadius: this.borderRadius,
          font: this.font
        });
      });
    }

    onTouchDown(e) {
      this.isDown = true;
      this.dragDistance = 0;
      this.scroll.position = this.scroll.current;
      this.start = e.touches ? e.touches[0].clientX : e.clientX;
      this.startY = e.touches ? e.touches[0].clientY : e.clientY;
    }

    onTouchMove(e) {
      if (!this.isDown) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = x - this.start;
      const dy = y - this.startY;
      this.dragDistance = Math.sqrt(dx * dx + dy * dy);

      const distance = (this.start - x) * (this.scrollSpeed * 0.025);
      this.scroll.target = this.scroll.position + distance;
    }

    onTouchUp(e) {
      if (this.isDown && this.dragDistance < 8) {
        const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;

        if (this.container) {
          const rect = this.container.getBoundingClientRect();
          const relX = clientX - rect.left;
          const relY = clientY - rect.top;

          let clickedMedia = null;
          let minDistance = Infinity;

          this.medias.forEach(media => {
            const pos = media.screenPos;
            if (
              relX >= pos.x && relX <= pos.x + pos.w &&
              relY >= pos.y && relY <= pos.y + pos.h + 50
            ) {
              const centerX = pos.x + pos.w / 2;
              const dist = Math.abs(relX - centerX);
              if (dist < minDistance) {
                minDistance = dist;
                clickedMedia = media;
              }
            }
          });

          if (clickedMedia && clickedMedia.url && clickedMedia.url !== '#') {
            window.location.href = clickedMedia.url;
          }
        }
      }

      this.isDown = false;
      this.onCheck();
    }

    onWheel(e) {
      const delta = e.deltaY || e.wheelDelta || e.detail;
      this.scroll.target += (delta > 0 ? this.scrollSpeed : -this.scrollSpeed) * 0.2;
      this.onCheckDebounce();
    }

    onKeyDown(e) {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          this.scroll.target += this.scrollSpeed * 5;
          this.onCheckDebounce();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.scroll.target -= this.scrollSpeed * 5;
          this.onCheckDebounce();
          break;
        default:
          break;
      }
    }

    onCheck() {
      if (!this.medias || !this.medias[0]) return;
      const width = this.medias[0].width;
      const itemIndex = Math.round(Math.abs(this.scroll.target) / width);
      const item = width * itemIndex;
      this.scroll.target = this.scroll.target < 0 ? -item : item;
    }

    onResize() {
      if (!this.container) return;
      this.screen = {
        width: this.container.clientWidth || window.innerWidth,
        height: this.container.clientHeight || 500
      };
      if (this.renderer) {
        this.renderer.setSize(this.screen.width, this.screen.height);
      }
      if (this.camera) {
        this.camera.perspective({
          aspect: this.screen.width / this.screen.height
        });
        const fov = (this.camera.fov * Math.PI) / 180;
        const height = 2 * Math.tan(fov / 2) * this.camera.position.z;
        const width = height * this.camera.aspect;
        this.viewport = { width, height };
      }
      if (this.medias) {
        this.medias.forEach(media => media.onResize({ screen: this.screen, viewport: this.viewport }));
      }
    }

    update() {
      this.scroll.current = lerp(this.scroll.current, this.scroll.target, this.scroll.ease);
      const direction = this.scroll.current > this.scroll.last ? 'right' : 'left';
      if (this.medias) {
        this.medias.forEach(media => media.update(this.scroll, direction, this.camera));
      }
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render({ scene: this.scene, camera: this.camera });
      }
      this.scroll.last = this.scroll.current;
      this.raf = window.requestAnimationFrame(this.update.bind(this));
    }

    addEventListeners() {
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnWheel = this.onWheel.bind(this);
      this.boundOnTouchDown = this.onTouchDown.bind(this);
      this.boundOnTouchMove = this.onTouchMove.bind(this);
      this.boundOnTouchUp = this.onTouchUp.bind(this);
      this.boundOnKeyDown = this.onKeyDown.bind(this);

      window.addEventListener('resize', this.boundOnResize);
      this.container.addEventListener('mousewheel', this.boundOnWheel, { passive: true });
      this.container.addEventListener('wheel', this.boundOnWheel, { passive: true });
      this.container.addEventListener('mousedown', this.boundOnTouchDown);
      window.addEventListener('mousemove', this.boundOnTouchMove);
      window.addEventListener('mouseup', this.boundOnTouchUp);
      this.container.addEventListener('touchstart', this.boundOnTouchDown, { passive: true });
      window.addEventListener('touchmove', this.boundOnTouchMove, { passive: true });
      window.addEventListener('touchend', this.boundOnTouchUp);
      this.container.addEventListener('keydown', this.boundOnKeyDown);
    }

    destroy() {
      window.cancelAnimationFrame(this.raf);
      window.removeEventListener('resize', this.boundOnResize);
      window.removeEventListener('mousemove', this.boundOnTouchMove);
      window.removeEventListener('mouseup', this.boundOnTouchUp);
      window.removeEventListener('touchmove', this.boundOnTouchMove);
      window.removeEventListener('touchend', this.boundOnTouchUp);

      if (this.renderer && this.renderer.gl && this.renderer.gl.canvas.parentNode) {
        this.renderer.gl.canvas.parentNode.removeChild(this.renderer.gl.canvas);
      }
    }
  }

  window.CircularGalleryApp = CircularGalleryApp;
})(window);
